import { z } from 'zod';
import { UPLOAD_MAX_IMAGE_FILES } from './stills.js';
/** Upload limits and accepted containers, as the creator screen states them. */
export const UPLOAD_MAX_BYTES = 2 * 1024 * 1024 * 1024;
export const UPLOAD_MAX_FILES = 20;
/**
 * A sample pack is hundreds of small files, so audio has its own ceiling: 20 was written for video,
 * where every file is hundreds of megabytes (Dax, 2026-09-20). The two are checked separately. A whole
 * pack goes in one drag (Dax, 2026-09-29): the drop is registered at once and sent in pages.
 */
export const UPLOAD_MAX_AUDIO_FILES = 5000;
/**
 * Files whose upload sessions one call opens. Every session is a round trip to Google, so a drop is sent
 * page by page, the next page opening while this one sends, rather than waiting on thousands at the start.
 */
export const UPLOAD_SESSION_PAGE = 50;
/** GCS resumable chunks must be multiples of 256 KiB. */
export const UPLOAD_CHUNK_BYTES = 8 * 1024 * 1024;
export const UPLOAD_RETRY_LIMIT = 5;
export const UPLOAD_RETRY_DELAY_MS = 1500;
export const VIDEO_MIME_BY_EXT = {
    mp4: 'video/mp4',
    mov: 'video/quicktime',
    m4v: 'video/x-m4v',
    webm: 'video/webm',
};
/** Audio drops become audio beds; the audio library is its own section. */
export const AUDIO_MIME_BY_EXT = {
    mp3: 'audio/mpeg',
    wav: 'audio/wav',
    m4a: 'audio/mp4',
    aac: 'audio/aac',
    ogg: 'audio/ogg',
    flac: 'audio/flac',
};
/**
 * Stills (Dax, 2026-10-05). The browser sends what it could convert as image/jpeg under the file's own name,
 * so a HEIC Safari converted still matches itself by name when dropped again; a HEIC it could not decode
 * travels as image/heic and the worker converts it.
 */
export const IMAGE_MIME_BY_EXT = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
    heic: 'image/heic',
    heif: 'image/heif',
};
/** The staging extension of each image type a browser may send. */
export const IMAGE_EXT_BY_MIME = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'image/heic': 'heic',
    'image/heif': 'heif',
};
/** Which kind of drop a file belongs to, by its extension; null when Studio takes no such file. */
export function uploadKindOf(name) {
    const ext = name.slice(name.lastIndexOf('.') + 1).toLowerCase();
    if (VIDEO_MIME_BY_EXT[ext])
        return 'video';
    if (IMAGE_MIME_BY_EXT[ext])
        return 'image';
    if (AUDIO_MIME_BY_EXT[ext])
        return 'audio';
    return null;
}
export const UploadFile = z.object({
    name: z.string().min(1).max(300),
    bytes: z.number().int().positive().max(UPLOAD_MAX_BYTES),
    contentType: z.string().min(1),
    /**
     * Where the file sat inside a dropped folder ("Foley/Glass/clink.wav"). A pack's folders are
     * taxonomy somebody already wrote, so audio ingest reads its tags from here. Null for a loose file.
     */
    path: z.string().max(1000).nullable().default(null),
});
export const UploadRequest = z.object({
    files: z.array(UploadFile).min(1).max(UPLOAD_MAX_AUDIO_FILES),
    /**
     * False brings the drop in without analysis (Dax, 2026-10-31): a quick preview and its keyframes, ready for Composer Fast.
     * Said up front, because a single clip starts its analysis the moment it lands.
     */
    autoAnalyse: z.boolean().default(true),
});
/**
 * One field of the "Tag this drop" modal the user changed: a brand facet key (actor, creator, brand,
 * flavour…) and the value it now holds, null when the field was cleared. Only changed fields travel, so
 * a field left alone never touches what the auto-tagger or an earlier hand put there.
 */
/** How many values one field of the tag modal may add or remove in one save. */
export const TAG_CHANGE_MAX_VALUES = 50;
const FacetValueLabel = z.string().trim().min(1).max(120);
/** One field of the tag modal: values added to every listed clip, values taken off them. */
export const AssetFacetChange = z.object({
    key: z.string().min(1).max(40),
    add: z.array(FacetValueLabel).max(TAG_CHANGE_MAX_VALUES).default([]),
    remove: z.array(FacetValueLabel).max(TAG_CHANGE_MAX_VALUES).default([]),
});
export const TAG_ASSETS_MAX_CHANGES = 12;
export const TagAssetsRequest = z.object({
    /** A drop's clips and stills together: its tags describe the whole shoot. */
    assetIds: z.array(z.string().uuid()).min(1).max(UPLOAD_MAX_FILES + UPLOAD_MAX_IMAGE_FILES),
    changes: z.array(AssetFacetChange).min(1).max(TAG_ASSETS_MAX_CHANGES),
});
export const UploadSessionsRequest = z.object({
    entries: z.array(z.object({ assetId: z.string().uuid(), partId: z.string().uuid().nullable() })).min(1).max(UPLOAD_SESSION_PAGE),
});
/**
 * Whether two places in dropped folders are the same file's: equal, or one ends with the other at a
 * folder boundary, so a pack dropped whole ("MOTION PULSE/Metal/Metal_01.wav") and by its subfolders
 * ("Metal/Metal_01.wav") are recognised as the same files.
 */
export function samePlace(a, b) {
    return a === b || a.endsWith(`/${b}`) || b.endsWith(`/${a}`);
}
/**
 * Whether a file dropped in a folder is a sound the library already holds (Dax, 2026-09-29). The name
 * alone is not enough: packs reuse "Water_01.wav" freely, so the size must agree and, when both carry
 * one, the place in the folder too. A sound that arrived loose has no place, and name and size decide.
 */
export function heldAlready(dropped, held) {
    if (held.originalFilename !== dropped.name || held.bytes !== dropped.bytes)
        return false;
    return held.sourcePath === null || samePlace(dropped.path, held.sourcePath);
}
/** How a drop of several video files is filed: each its own clip, or all of them one project. */
export const UploadGrouping = z.enum(['separate', 'project']);
/** A batch left undecided this long (the tab closed on the modal) is filed as separate clips. */
export const UPLOAD_DECISION_TIMEOUT_MS = 10 * 60 * 1000;
/**
 * An upload that has not landed this long is abandoned: a resumable session lives one week, so nothing
 * can finish it after this. The worker's hourly sweep removes it, and any staged file nobody references.
 */
export const UPLOAD_ABANDONED_AFTER_MS = 7 * 24 * 60 * 60 * 1000;
const PROJECT_NAME_MIN_CHARS = 3;
/**
 * A project's name from its files: the stem the natural-sorted names share ("IMG_0412", "IMG_0413"
 * give "IMG_04"; "Shoot day part 1", "Shoot day part 2" give "Shoot day"), trimmed of trailing
 * separators, digits and part words; the first name when they share too little.
 */
export function projectName(names) {
    const cleaned = names.map((n) => n.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim()).filter(Boolean);
    if (!cleaned.length)
        return 'Project';
    const sorted = [...cleaned].sort((a, b) => a.localeCompare(b, 'en', { numeric: true, sensitivity: 'base' }));
    const first = sorted[0];
    let common = first;
    for (const n of sorted.slice(1)) {
        let i = 0;
        while (i < common.length && i < n.length && common[i].toLowerCase() === n[i].toLowerCase())
            i++;
        common = common.slice(0, i);
    }
    const stem = common.replace(/(\s*(part|pt|clip|take|no|#)\s*)?[\s\d.#()-]*$/i, '').trim();
    return stem.length >= PROJECT_NAME_MIN_CHARS ? stem : first;
}
/** Packs (imports) listed in Browse's Pack menu: a drop of fewer files is opened from its Upload row instead. */
export const IMPORT_PACK_MIN_FILES = 10;
/**
 * A drop's pack name: the folder every file came from, when they share one ("Ghosthack Foley/Chains/…"
 * gives "Ghosthack Foley"). Null when the files came from different folders or none, for the caller to
 * fall back on.
 */
export function packName(paths) {
    const tops = new Set(paths.map((p) => (p && p.includes('/') ? p.slice(0, p.indexOf('/')).trim() : '')));
    const [only] = [...tops];
    return tops.size === 1 && only ? only : null;
}
//# sourceMappingURL=upload.js.map